import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import apiClient from '../api/api';
import { useAuth } from '../context/AuthContext';
import { FaShieldAlt } from 'react-icons/fa';

function VerifyOtpPage() {
    const [otp, setOtp] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [resendCooldown, setResendCooldown] = useState(60);

    const navigate = useNavigate();
    const location = useLocation();
    const { login } = useAuth();
    
    // The login identifier (phone or email) should be passed from the registration page
    const loginIdentifier = location.state?.login;

    useEffect(() => {
        if (!loginIdentifier) {
            setError("No phone number or email provided. Please go back to registration.");
        }
    }, [loginIdentifier]);

    useEffect(() => {
        let timer;
        if (resendCooldown > 0) {
            timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
        }
        return () => clearTimeout(timer);
    }, [resendCooldown]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');
        setSuccess('');

        if (!otp || otp.length !== 6) {
            setError('Please enter a valid 6-digit OTP.');
            setLoading(false);
            return;
        }

        try {
            const response = await apiClient.post('/auth/verify-otp', { login: loginIdentifier, otp });
            if (response.status === 200 && response.data.userId) {
                setSuccess('Verification successful! Redirecting...');
                // The login function from the context now handles setting state and redirecting
                login(response.data, navigate);
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Verification failed. Please try again.');
            setLoading(false);
        }
    };

    const handleResendOtp = async () => {
        if (resendCooldown > 0) return;

        setLoading(true);
        setError('');
        try {
            await apiClient.post('/auth/resend-otp', { login: loginIdentifier });
            setSuccess('A new OTP has been sent.');
            setResendCooldown(60); // Reset cooldown
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to resend OTP.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-emerald-500 to-teal-900 flex items-center justify-center p-6">
            <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden transform transition-all hover:scale-[1.01]">
                <div className="bg-emerald-600 p-6 flex flex-col items-center">
                    <div className="bg-white p-3 rounded-full shadow-md mb-3">
                        <FaShieldAlt className="text-emerald-600 text-3xl" />
                    </div>
                    <h2 className="text-2xl font-bold text-white text-center">Security Verification</h2>
                    <p className="text-emerald-100 text-sm text-center mt-1">Protecting your account</p>
                </div>

                <div className="p-8">
                    <p className="text-center text-gray-600 mb-8">
                        We have sent a 6-digit verification code to <br/>
                        <span className="font-bold text-gray-800 bg-gray-100 px-2 py-1 rounded">{loginIdentifier}</span>
                    </p>

                    <form onSubmit={handleSubmit} className="space-y-6">
                        <div className="relative">
                            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 block">Enter Code</label>
                            <input
                                type="text"
                                placeholder="000000"
                                value={otp}
                                onChange={(e) => {
                                    const val = e.target.value.replace(/[^0-9]/g, '');
                                    if (val.length <= 6) setOtp(val);
                                }}
                                className="w-full text-center tracking-[0.75rem] font-mono text-3xl border-2 border-gray-200 rounded-xl py-4 focus:border-emerald-500 focus:ring-4 focus:ring-emerald-100 transition-all outline-none placeholder-gray-300 text-gray-800"
                                maxLength="6"
                                required
                                disabled={!loginIdentifier}
                            />
                        </div>

                        {error && (
                            <div className="bg-red-50 border-l-4 border-red-500 text-red-700 p-3 text-sm rounded">
                                {error}
                            </div>
                        )}
                        {success && (
                            <div className="bg-green-50 border-l-4 border-green-500 text-green-700 p-3 text-sm rounded">
                                {success}
                            </div>
                        )}

                        <button 
                            type="submit" 
                            className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-bold py-4 rounded-xl shadow-lg hover:shadow-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed transform active:scale-95"
                            disabled={loading || !loginIdentifier || otp.length !== 6}
                        >
                            {loading ? (
                                <span className="flex items-center justify-center">
                                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                    Verifying...
                                </span>
                            ) : 'Verify Code'}
                        </button>

                        <div className="flex flex-col items-center justify-center space-y-4 pt-4 border-t border-gray-100">
                            <p className="text-gray-500 text-sm">
                                Didn't receive the code?{' '}
                                <button
                                    type="button"
                                    onClick={handleResendOtp}
                                    disabled={resendCooldown > 0 || loading}
                                    className="font-semibold text-emerald-600 hover:text-emerald-800 disabled:text-gray-400 disabled:cursor-not-allowed transition-colors"
                                >
                                    {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend OTP'}
                                </button>
                            </p>
                            <Link to="/register" className="text-sm font-medium text-gray-400 hover:text-gray-600 transition-colors">
                                &larr; Back to Registration
                            </Link>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
}

export default VerifyOtpPage;